import { Fragment, useState } from 'react';
import PageHeader from '../components/PageHeader.jsx';
import Card from '../components/Card.jsx';
import { SelectField, Options, Cell } from '../components/Field.jsx';
import { Loader, ErrorAlert, EmptyRow, InfoAlert } from '../components/States.jsx';
import useRowEditor from '../hooks/useRowEditor.js';
import { useListGroupedPricesQuery, useSavePricesMutation, useDeletePriceMutation } from '../store/apiSlice.js';
import { priceRowSchema } from '../validation/schemas.js';
import { validateRowSet, remapRowErrors, rowError } from '../validation/rules.js';
import { useToast } from '../context/ToastContext.jsx';
import { useReferenceData } from '../store/useReferenceData.js';
import { money, dash } from '../utils/format.js';

const COMMON = 'Common';
const blankRow = () => ({ product: '', texture: '', color: '', length: '', price: '', gst: '18' });

export default function PricesPage() {
  const showToast = useToast();
  const { products, textures, lengths, colors, vendors, customers } = useReferenceData();
  const { data: groups, isLoading: loading, error: listError, refetch: reload } = useListGroupedPricesQuery();
  const error = listError?.message ?? null;

  const [savePrices] = useSavePricesMutation();
  const [deletePriceMutation] = useDeletePriceMutation();

  const [vendor, setVendor] = useState('');
  const [customer, setCustomer] = useState(COMMON);
  const [busy, setBusy] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [headerError, setHeaderError] = useState('');
  const { rows, addRow, removeRow, patchRow, resetRows } = useRowEditor(blankRow);

  const err = (index, field) => rowError(rowErrors, 'rows', index, field);

  /** Clear a cell's error the moment the user edits that cell. */
  const editCell = (row, index, field, value) => {
    patchRow(row._key, { [field]: value });
    setRowErrors((current) => {
      const cell = `rows.${index}.${field}`;
      if (!current[cell]) return current;
      const next = { ...current };
      delete next[cell];
      return next;
    });
  };

  const save = async () => {
    if (!vendor && !customer) {
      setHeaderError('Select a vendor or a customer');
      showToast('Select a vendor or a customer', 'error');
      return;
    }
    setHeaderError('');

    const { errors, payload, keep } = validateRowSet(rows, priceRowSchema, { key: 'rows' });
    if (!payload.length) {
      setRowErrors({});
      showToast('Add at least one row with a product and a price', 'error');
      return;
    }
    setRowErrors(errors);
    if (Object.keys(errors).length) {
      showToast('Please fix the highlighted fields', 'error');
      return;
    }

    setBusy(true);
    try {
      const body = payload.map(({ _key, ...row }) => ({
        ...row,
        price: Number(row.price),
        gst: row.gst === '' ? 18 : Number(row.gst),
      }));
      const result = await savePrices({ vendor, customer, rows: body }).unwrap();
      const parts = [];
      if (result.created) parts.push(`${result.created} added`);
      if (result.updated) parts.push(`${result.updated} updated`);
      showToast(`Prices saved — ${parts.join(', ')}`);
      resetRows();
      setRowErrors({});
    } catch (apiError) {
      const details = apiError.details || {};
      setRowErrors(remapRowErrors(details, 'rows', keep));
      if (details.vendor || details.customer) setHeaderError(details.vendor || details.customer);
      showToast(apiError.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const deletePrice = async (id) => {
    if (!window.confirm('Delete this price?')) return;
    try {
      await deletePriceMutation(id).unwrap();
      showToast('Price deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Pricing"
        title="Price Management"
        subtitle="Pick a vendor and customer once, then enter prices in the table. All prices are GST inclusive."
      />

      <div className="selector-bar">
        <SelectField label="Vendor" value={vendor} error={headerError} onChange={(e) => { setVendor(e.target.value); setHeaderError(''); }}>
          <Options items={vendors} placeholder="Select vendor" />
        </SelectField>
        <SelectField label="Customer" value={customer} onChange={(e) => { setCustomer(e.target.value); setHeaderError(''); }}>
          <option value="">Select customer</option>
          <option value={COMMON}>Common (default price list)</option>
          {customers.map((c) => (
            <option key={c.company} value={c.company}>
              {c.company} {c.customerType === 'Regular' ? '· Regular' : '· Standard'}
            </option>
          ))}
        </SelectField>
        <div style={{ alignSelf: 'flex-end', color: 'var(--muted)', fontSize: '11.5px', paddingBottom: 2 }}>
          Saving the same combination again updates its price instead of adding a duplicate.
        </div>
      </div>

      {customer && customer !== COMMON && customers.find((c) => c.company === customer)?.customerType === 'Standard' && (
        <InfoAlert>
          <strong>{customer}</strong> is a Standard customer, so Stock Out will always use the{' '}
          <strong>Common</strong> price list. Change the customer type to Regular for custom pricing to take effect.
        </InfoAlert>
      )}

      <Card title="Price Entry Rows" tag="GST Inclusive">
        <div style={{ overflowX: 'auto' }}>
          <table className="entry-table">
            <thead>
              <tr>
                <th>Product Name *</th>
                <th>Texture *</th>
                <th>Color *</th>
                <th>Length *</th>
                <th>Price (₹ incl. GST) *</th>
                <th>GST %</th>
                <th style={{ width: 36 }} aria-label="Remove row" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row._key}>
                  {['product', 'texture', 'color', 'length'].map((field) => (
                    <Cell key={field} error={err(index, field)}>
                      <select
                        value={row[field]}
                        onChange={(e) => editCell(row, index, field, e.target.value)}
                        className={err(index, field) ? 'invalid' : undefined}
                        aria-label={field}
                      >
                        <Options
                          items={{ product: products, texture: textures, color: colors, length: lengths }[field]}
                          placeholder={field}
                        />
                      </select>
                    </Cell>
                  ))}
                  <Cell error={err(index, 'price')}>
                    <input
                      type="number" min="0" step="0.01" placeholder="0"
                      value={row.price}
                      onChange={(e) => editCell(row, index, 'price', e.target.value)}
                      className={err(index, 'price') ? 'invalid' : undefined}
                      aria-label="Price"
                    />
                  </Cell>
                  <Cell error={err(index, 'gst')}>
                    <input
                      type="number" min="0" max="100" step="0.01" placeholder="18"
                      value={row.gst}
                      onChange={(e) => editCell(row, index, 'gst', e.target.value)}
                      className={err(index, 'gst') ? 'invalid' : undefined}
                      aria-label="GST percent"
                    />
                  </Cell>
                  <td>
                    <button type="button" className="row-remove" onClick={() => removeRow(row._key)} disabled={rows.length === 1} aria-label="Remove row">
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="btn-row">
          <button type="button" className="btn ghost" onClick={addRow}>
            + Add Row
          </button>
          <button type="button" className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save Prices'}
          </button>
        </div>
      </Card>

      <Card title="Price List" tag="Click View to see length-wise prices">
        <ErrorAlert message={error} onRetry={reload} />
        {loading ? (
          <Loader />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Vendor Name</th>
                  <th>Customer Name</th>
                  <th>Product Name</th>
                  <th>Texture</th>
                  <th>Colour</th>
                  <th style={{ width: 80 }}>View</th>
                </tr>
              </thead>
              <tbody>
                {!groups?.length ? (
                  <EmptyRow colSpan={6}>Add your first price above</EmptyRow>
                ) : (
                  groups.map((group) => {
                    const open = openPanel === group.key;
                    return (
                      <Fragment key={group.key}>
                        <tr>
                          <td>{dash(group.vendor)}</td>
                          <td>{dash(group.customer)}</td>
                          <td>{group.product}</td>
                          <td>{dash(group.texture)}</td>
                          <td>{dash(group.color)}</td>
                          <td>
                            <button
                              type="button"
                              className="view-btn"
                              aria-expanded={open}
                              onClick={() => setOpenPanel(open ? null : group.key)}
                            >
                              {open ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={6} style={{ padding: 0, border: 'none' }}>
                            <div className={`expand-panel${open ? ' open' : ''}`}>
                              <div className="expand-heading">
                                Product: {group.product} &nbsp;·&nbsp; Texture: {dash(group.texture)} &nbsp;·&nbsp; Color:{' '}
                                {dash(group.color)}
                              </div>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Length</th>
                                    <th>Price</th>
                                    <th style={{ width: 70 }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.entries.map((entry) => (
                                    <tr key={entry.id}>
                                      <td>{dash(entry.length)}</td>
                                      <td className="num">
                                        ₹{money(entry.price)}{' '}
                                        <small className="muted">(GST {entry.gst}% incl.)</small>
                                      </td>
                                      <td>
                                        <button type="button" className="action-btn delete" onClick={() => deletePrice(entry.id)}>
                                          Delete
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                              <div className="gst-note">All prices are GST inclusive.</div>
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
